import React, { useState } from 'react';
import { CheckCircleIcon, CreditCardIcon, SmartphoneIcon, MonitorIcon, GlobeIcon, PlusIcon, TrashIcon, CalendarIcon, AlertCircleIcon } from 'lucide-react';
import Card from '../ui/Card';
// Logo URLs
const LOGO_URLS: Record<string, string> = {
  visa: 'https://cdn.magicpatterns.com/uploads/6Jyf8H7pdisihXCruML7ki/Visa_Logo.png',
  mastercard: 'https://cdn.magicpatterns.com/uploads/pqVbVKRhUKq9bTC69XhGHm/MasterCard_Logo.png',
  unionpay: 'https://cdn.magicpatterns.com/uploads/2q6qGw9eWh5449DuUzy5hc/UnionPay_Logo.png',
  paypak: 'https://cdn.magicpatterns.com/uploads/9VSJiwBepQTk8oQ33nJN35/PayPak_Logo.png',
  raast: 'https://cdn.magicpatterns.com/uploads/aTm9x6z4SPWciMjxCauCT8/RAAST_Logo.png'
};
export interface MDRConfig {
  id: string;
  mode: string;
  schemes: string[];
  mdr: string;
  effectiveDate: string;
  endDate: string;
  transactionType: string;
}
interface PaymentMethodsTabProps {
  configs?: MDRConfig[];
  onChange?: (configs: MDRConfig[]) => void;
}
const PAYMENT_MODES = [{
  id: 'pos',
  label: 'POS',
  icon: CreditCardIcon
}, {
  id: 'mobile_pos',
  label: 'Mobile POS',
  icon: SmartphoneIcon
}, {
  id: 'soft_pos',
  label: 'Soft POS',
  icon: MonitorIcon
}, {
  id: 'online',
  label: 'Online',
  icon: GlobeIcon
}, {
  id: 'raast',
  label: 'RAAST',
  icon: null
}];
const SCHEMES = [{
  id: 'visa',
  label: 'Visa'
}, {
  id: 'mastercard',
  label: 'Mastercard'
}, {
  id: 'unionpay',
  label: 'UnionPay'
}, {
  id: 'paypak',
  label: 'PayPak'
}];
const TRANSACTION_TYPES = ['All', 'OnUs', 'OffUs'];
export const PaymentMethodsTab: React.FC<PaymentMethodsTabProps> = ({
  configs = [],
  onChange
}) => {
  const [savedConfigs, setSavedConfigs] = useState<MDRConfig[]>(configs);
  const [selectedMode, setSelectedMode] = useState<string | null>(null);
  const [selectedSchemes, setSelectedSchemes] = useState<string[]>([]);
  const [sharedDraft, setSharedDraft] = useState({
    mdr: '',
    effectiveDate: '',
    endDate: '',
    transactionType: 'All'
  });
  const [raastDraft, setRaastDraft] = useState({
    mdr: '',
    effectiveDate: '',
    endDate: '',
    transactionType: 'All'
  });
  const [error, setError] = useState<string | null>(null);
  const handleModeSelect = (modeId: string) => {
    setSelectedMode(modeId);
    setSelectedSchemes([]);
    setSharedDraft({
      mdr: '',
      effectiveDate: '',
      endDate: '',
      transactionType: 'All'
    });
    setRaastDraft({
      mdr: '',
      effectiveDate: '',
      endDate: '',
      transactionType: 'All'
    });
    setError(null);
  };
  const handleSchemeToggle = (schemeId: string) => {
    setSelectedSchemes(prev => {
      const isSelected = prev.includes(schemeId);
      return isSelected ? prev.filter(id => id !== schemeId) : [...prev, schemeId];
    });
    setError(null);
  };
  const handleSharedDraftChange = (field: string, value: string) => {
    if (field === 'mdr' && value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) return;
    setSharedDraft(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };
  const handleRaastDraftChange = (field: string, value: string) => {
    if (field === 'mdr' && value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) return;
    setRaastDraft(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };
  const checkOverlap = (newConfig: Omit<MDRConfig, 'id'>) => {
    const newStart = new Date(newConfig.effectiveDate).getTime();
    const newEnd = newConfig.endDate ? new Date(newConfig.endDate).getTime() : Infinity;
    return savedConfigs.some(config => {
      if (config.mode !== newConfig.mode || config.transactionType !== newConfig.transactionType) return false;
      // Check if any schemes overlap
      const hasSchemeOverlap = config.schemes.some(s => newConfig.schemes.includes(s));
      if (!hasSchemeOverlap) return false;
      const existingStart = new Date(config.effectiveDate).getTime();
      const existingEnd = config.endDate ? new Date(config.endDate).getTime() : Infinity;
      return newStart <= existingEnd && newEnd >= existingStart;
    });
  };
  const handleSaveConfigurations = () => {
    if (!selectedMode) return;
    const modeLabel = PAYMENT_MODES.find(m => m.id === selectedMode)?.label || selectedMode;
    const isRaast = selectedMode === 'raast';
    const draft = isRaast ? raastDraft : sharedDraft;
    const schemeLabels = isRaast ? ['RAAST'] : selectedSchemes.map(sid => SCHEMES.find(s => s.id === sid)?.label || sid);
    if (schemeLabels.length === 0) {
      setError('Please select at least one scheme.');
      return;
    }
    if (!draft.mdr || !draft.effectiveDate) {
      setError('Please fill in MDR Rate and Effective Date.');
      return;
    }
    if (draft.endDate && new Date(draft.endDate) <= new Date(draft.effectiveDate)) {
      setError('End date must be after the effective date.');
      return;
    }
    const newConfig: Omit<MDRConfig, 'id'> = {
      mode: modeLabel,
      schemes: schemeLabels,
      mdr: draft.mdr,
      effectiveDate: draft.effectiveDate,
      endDate: draft.endDate,
      transactionType: draft.transactionType
    };
    if (checkOverlap(newConfig)) {
      setError(`Date range overlaps with an existing configuration for ${modeLabel} — ${schemeLabels.join(', ')} (${draft.transactionType}).`);
      return;
    }
    const updatedConfigs = [...savedConfigs, {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      ...newConfig
    }];
    setSavedConfigs(updatedConfigs);
    if (onChange) onChange(updatedConfigs);
    // Reset
    setSelectedSchemes([]);
    setSharedDraft({
      mdr: '',
      effectiveDate: '',
      endDate: '',
      transactionType: 'All'
    });
    setRaastDraft({
      mdr: '',
      effectiveDate: '',
      endDate: '',
      transactionType: 'All'
    });
    setError(null);
  };
  const removeConfig = (id: string) => {
    const updatedConfigs = savedConfigs.filter(c => c.id !== id);
    setSavedConfigs(updatedConfigs);
    if (onChange) onChange(updatedConfigs);
  };
  const hasConfigForMode = (modeLabel: string) => {
    return savedConfigs.some(c => c.mode === modeLabel);
  };
  const renderSchemeLogo = (schemeId: string, size: 'sm' | 'md' = 'md') => {
    const url = LOGO_URLS[schemeId];
    if (!url) return null;
    const h = size === 'sm' ? 'h-5' : 'h-7';
    return <img src={url} alt={schemeId} className={`${h} w-auto object-contain`} data-id="element-461" />;
  };
  const getSchemeIdFromLabel = (label: string): string => {
    const found = SCHEMES.find(s => s.label === label);
    if (found) return found.id;
    if (label === 'RAAST') return 'raast';
    return label.toLowerCase();
  };
  const renderMDRForm = (draft: {
    mdr: string;
    effectiveDate: string;
    endDate: string;
    transactionType: string;
  }, onChangeFn: (field: string, value: string) => void, label: string) => <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-5" data-id="element-462">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4" data-id="element-463">
        {label}
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-464">
        <div data-id="element-465">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-466">
            MDR Rate (%) <span className="text-red-500" data-id="element-467">*</span>
          </label>
          <div className="relative" data-id="element-468">
            <input type="text" value={draft.mdr} onChange={e => onChangeFn('mdr', e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-3 pr-8 text-sm text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500" placeholder="0.00" data-id="element-469" />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-470">
              <span className="text-gray-500 dark:text-gray-400 text-sm" data-id="element-471">
                %
              </span>
            </div>
          </div>
        </div>

        <div data-id="element-472">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-473">
            Effective Date <span className="text-red-500" data-id="element-474">*</span>
          </label>
          <div className="relative" data-id="element-475">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-476">
              <CalendarIcon className="h-4 w-4 text-gray-400" data-id="element-477" />
            </div>
            <input type="date" value={draft.effectiveDate} onChange={e => onChangeFn('effectiveDate', e.target.value)} min={new Date().toISOString().split('T')[0]} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-10 pr-3 text-sm text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500" data-id="element-478" />
          </div>
        </div>

        <div data-id="element-479">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-480">
            End Date
          </label>
          <div className="relative" data-id="element-481">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-482">
              <CalendarIcon className="h-4 w-4 text-gray-400" data-id="element-483" />
            </div>
            <input type="date" value={draft.endDate} onChange={e => onChangeFn('endDate', e.target.value)} min={draft.effectiveDate || new Date().toISOString().split('T')[0]} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-10 pr-3 text-sm text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500" data-id="element-484" />
          </div>
        </div>

        <div data-id="element-485">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-486">
            Transaction Type <span className="text-red-500" data-id="element-487">*</span>
          </label>
          <select value={draft.transactionType} onChange={e => onChangeFn('transactionType', e.target.value)} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm text-gray-900 dark:text-white focus:ring-blue-500 focus:border-blue-500" data-id="element-488">
            {TRANSACTION_TYPES.map(type => <option key={type} value={type} data-id="element-489">
                {type}
              </option>)}
          </select>
        </div>
      </div>
    </div>;
  return <Card data-id="element-490">
      <div className="space-y-8" data-id="element-491">
        {/* Step 1: Payment Mode Selection */}
        <div data-id="element-492">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4" data-id="element-493">
            1. Select Payment Mode
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4" data-id="element-494">
            {PAYMENT_MODES.map(mode => {
            const Icon = mode.icon;
            const isSelected = selectedMode === mode.id;
            const hasConfig = hasConfigForMode(mode.label);
            const isRaastMode = mode.id === 'raast';
            return <button key={mode.id} type="button" onClick={() => handleModeSelect(mode.id)} className={`relative flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all duration-200 ${isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-700'}`} data-id="element-495">
                  {hasConfig && <div className="absolute -top-2 -right-2 bg-white dark:bg-gray-800 rounded-full" data-id="element-496">
                      <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-497" />
                    </div>}
                  {isRaastMode ? <img src={LOGO_URLS.raast} alt="RAAST" className="h-8 w-auto object-contain mb-2" data-id="element-498" /> : Icon ? <Icon className={`h-8 w-8 mb-2 ${isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-499" /> : null}
                  <span className={`text-sm font-medium ${isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'}`} data-id="element-500">
                    {mode.label}
                  </span>
                </button>;
          })}
          </div>
        </div>

        {/* Step 2: Scheme Selection (Only if non-RAAST mode is selected) */}
        {selectedMode && selectedMode !== 'raast' && <div data-id="element-501">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4" data-id="element-502">
              2. Select Schemes
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-503">
              {SCHEMES.map(scheme => {
            const isSelected = selectedSchemes.includes(scheme.id);
            return <button key={scheme.id} type="button" onClick={() => handleSchemeToggle(scheme.id)} className={`relative flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all duration-200 ${isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-700'}`} data-id="element-504">
                    {isSelected && <div className="absolute -top-2 -right-2 bg-white dark:bg-gray-800 rounded-full" data-id="element-505">
                        <CheckCircleIcon className="h-4 w-4 text-blue-500" data-id="element-506" />
                      </div>}
                    <div className="h-8 flex items-center justify-center mb-2" data-id="element-507">
                      {renderSchemeLogo(scheme.id)}
                    </div>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 text-center" data-id="element-508">
                      {scheme.label}
                    </span>
                  </button>;
          })}
            </div>
          </div>}

        {/* Step 3: MDR Configuration (shared form for all selected schemes) */}
        {selectedMode && selectedMode !== 'raast' && selectedSchemes.length > 0 && <div data-id="element-509">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4" data-id="element-510">
                3. Configure MDR &amp; Transaction Type
              </h3>
              <div className="mb-3 flex flex-wrap gap-2 items-center" data-id="element-511">
                <span className="text-xs text-gray-500 dark:text-gray-400 mr-1" data-id="element-512">
                  Applying to:
                </span>
                {selectedSchemes.map(sid => {
            const scheme = SCHEMES.find(s => s.id === sid);
            return <span key={sid} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" data-id="element-513">
                      {renderSchemeLogo(sid, 'sm')}
                      {scheme?.label}
                    </span>;
          })}
              </div>
              {renderMDRForm(sharedDraft, handleSharedDraftChange, 'MDR Details')}
            </div>}

        {/* RAAST MDR Configuration */}
        {selectedMode === 'raast' && <div data-id="element-514">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4" data-id="element-515">
              2. Configure RAAST MDR &amp; Transaction Type
            </h3>
            {renderMDRForm(raastDraft, handleRaastDraftChange, 'RAAST MDR Details')}
          </div>}

        {/* Error */}
        {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 flex items-start" data-id="element-516">
            <AlertCircleIcon className="h-5 w-5 text-red-500 dark:text-red-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-517" />
            <p className="text-sm text-red-700 dark:text-red-300" data-id="element-518">{error}</p>
          </div>}

        {/* Save Button */}
        {selectedMode && (selectedMode === 'raast' || selectedSchemes.length > 0) && <div className="flex justify-end" data-id="element-519">
              <button type="button" onClick={handleSaveConfigurations} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-520">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-521" />
                Add to Table
              </button>
            </div>}

        {/* Saved Configurations Table */}
        {savedConfigs.length > 0 && <div className="border-t border-gray-200 dark:border-gray-700 pt-8 mt-8" data-id="element-522">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4" data-id="element-523">
              Configured Payment Methods
            </h3>
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-524">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-525">
                <thead className="bg-gray-50 dark:bg-gray-800" data-id="element-526">
                  <tr data-id="element-527">
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-528">
                      Mode
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-529">
                      Scheme(s)
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-530">
                      MDR
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-531">
                      Txn Type
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-532">
                      Effective Date
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-533">
                      End Date
                    </th>
                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-534">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-535">
                  {savedConfigs.map(config => <tr key={config.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-536">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-537">
                        {config.mode}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300" data-id="element-538">
                        <div className="flex flex-wrap items-center gap-2" data-id="element-539">
                          {config.schemes.map(schemeLabel => {
                      const schemeId = getSchemeIdFromLabel(schemeLabel);
                      return <span key={schemeLabel} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-xs font-medium text-gray-700 dark:text-gray-300" data-id="element-540">
                                {renderSchemeLogo(schemeId, 'sm')}
                                {schemeLabel}
                              </span>;
                    })}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-blue-600 dark:text-blue-400" data-id="element-541">
                        {config.mdr}%
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-542">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.transactionType === 'All' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' : config.transactionType === 'OnUs' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'}`} data-id="element-543">
                          {config.transactionType}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-544">
                        {new Date(config.effectiveDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" data-id="element-545">
                        {config.endDate ? new Date(config.endDate).toLocaleDateString() : <span className="text-gray-400 italic" data-id="element-546">
                            No end date
                          </span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium" data-id="element-547">
                        <button type="button" onClick={() => removeConfig(config.id)} className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" title="Remove configuration" data-id="element-548">
                          <TrashIcon className="h-5 w-5" data-id="element-549" />
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}
      </div>
    </Card>;
};